import { useEffect, useMemo, useState } from "react";
import Microlink from "@microlink/react";

// The paid microlink key shared by every preview surface. All callers draw from
// the same quota, which is exactly why the cache below matters.
const DEFAULT_MICROLINK_API_KEY = "JgUARf2HjedOQkin6wQs2Ll11upPpWG5gJ1NTX9c";

// Matches the LinkedIn uplift card debounce so every surface behaves the same:
// a live-typed URL settles before we let it change what we fetch.
const DEFAULT_DEBOUNCE_MS = 800;

type MicrolinkData = Record<string, unknown>;

// Module-level, so the dedupe is shared across every CachedMicrolink instance:
// once any surface (composer, modal, post details, inbox) has fetched a URL, later
// mounts render from here with fetchData={false} and issue no further request for
// the rest of the browser session, no matter how many times they remount.
const resolvedCache = new Map<string, MicrolinkData>();

// Test-only: clears the module-level cache so test order can't leak state.
export const __resetMicrolinkCacheForTests = (): void => {
  resolvedCache.clear();
};

const normalizeUrl = (url: string): string =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

// Guards against firing for half-typed URLs (`https://a`, `www.`) — only render
// once the host has a dot with a plausible TLD after it.
const isFetchableUrl = (url: string): boolean => {
  try {
    const { hostname } = new URL(normalizeUrl(url));

    return /\.[^.\s]{2,}$/.test(hostname);
  } catch {
    return false;
  }
};

type CachedMicrolinkProps = {
  url: string;
  apiKey?: string;
  debounce?: number;
  className?: string;
  media?: string[];
  [key: string]: unknown;
};

/**
 * Drop-in replacement for `<Microlink>` that shares one session-scoped fetch
 * cache across every instance and debounces live-typed URL changes.
 *
 * The card still renders immediately: on a cache miss we let `<Microlink>` run
 * its own single fetch and capture the result into the shared cache via
 * `setData`; on a cache hit we hand it the cached payload with
 * `fetchData={false}`, so it never issues a per-mount network request. Only URL
 * *changes* are debounced, so committed URLs (inbox, post details) render with
 * no delay while a URL typed into a field settles before refetching.
 */
export function CachedMicrolink({
  url,
  apiKey = DEFAULT_MICROLINK_API_KEY,
  debounce = DEFAULT_DEBOUNCE_MS,
  ...rest
}: CachedMicrolinkProps): JSX.Element | null {
  // Initialised to the first url so the initial render is synchronous (no delay
  // or flash); only subsequent url changes are debounced.
  const [debouncedUrl, setDebouncedUrl] = useState(url);

  useEffect(() => {
    if (url === debouncedUrl) {
      return;
    }

    // Known URLs need no debounce; unknown ones wait for the typing to settle.
    const delay = resolvedCache.has(normalizeUrl(url)) ? 0 : debounce;
    const timeoutId = setTimeout(() => setDebouncedUrl(url), delay);

    return () => clearTimeout(timeoutId);
  }, [url, debouncedUrl, debounce]);

  const key = normalizeUrl(debouncedUrl);
  const cachedData = resolvedCache.get(key);

  const provideData = useMemo(() => {
    if (cachedData) {
      // Cache hit: feed the stored payload, ignore microlink's (skipped) fetch.
      return () => cachedData;
    }

    // Cache miss: capture microlink's fetched payload for later mounts.
    return (fetched: unknown): MicrolinkData => {
      if (fetched && typeof fetched === "object") {
        resolvedCache.set(key, fetched as MicrolinkData);
      }

      return (fetched as MicrolinkData) ?? {};
    };
  }, [key, cachedData]);

  if (!isFetchableUrl(debouncedUrl)) {
    return null;
  }

  // Relies on the @microlink/react contract (verified against its compiled
  // source): with fetchData={true} it calls setData(fetchedPayload) after its
  // own fetch and renders the returned value; with fetchData={false} it skips
  // the network and calls setData() to obtain the payload to render. Pass the
  // normalized `key` so the fetched/displayed URL matches the cache key.
  return (
    <Microlink
      url={key}
      apiKey={apiKey}
      fetchData={!cachedData}
      setData={provideData}
      {...rest}
    />
  );
}

export default CachedMicrolink;
